// API service for backend communication
function resolveBaseURL() {
  const env = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
    ? process.env.REACT_APP_API_URL.trim()
    : '';
  let base = env;
  if (!base || !/^https?:\/\//.test(base)) {
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    const isLocalFront = /localhost:(3000|3001|3002|3003)/.test(origin || '');
    base = isLocalFront ? 'https://jobit-vniz.onrender.com/api' : (origin ? origin.replace(/\/+$/, '') + '/api' : 'https://jobit-vniz.onrender.com/api');
  }
  if (!/\/api(\/?$)/.test(base)) {
    base = base.replace(/\/+$/, '') + '/api';
  }
  return base.replace(/\/+$/, '');
}

class ApiService {
  constructor() {
    this.baseURL = resolveBaseURL();
    // Gate verbose logs behind dev mode and optional flag
    this.debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production')
      && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
    try { if (this.debug) console.log('[ApiService] Base URL:', this.baseURL); } catch (_) { /* no-op */ }
    this._healthy = false;
  }

  // Determine token/refresh keys and refresh endpoint based on endpoint/context
  getAuthContext(endpoint = '') {
    const hasUserToken = !!localStorage.getItem('token');
    const hasAdminToken = !!localStorage.getItem('adminToken');
    // For admin endpoints, ALWAYS use admin context (do not fall back to user)
    const preferAdmin = this.isAdminEndpoint(endpoint);
    const isAdmin = preferAdmin ? true : (hasAdminToken && !hasUserToken);
    return {
      isAdmin,
      accessKey: isAdmin ? 'adminToken' : 'token',
      refreshKey: isAdmin ? 'adminRefreshToken' : 'refreshToken',
      refreshEndpoint: isAdmin ? '/admin-auth/refresh' : '/auth/refresh',
    };
  }

  // Determine if the endpoint should use admin auth (prefers admin token when both exist)
  isAdminEndpoint(endpoint = '') {
    if (!endpoint || typeof endpoint !== 'string') return false;
    const adminPatterns = [
      /^\/admin(\/?|$)/,
      /^\/admin-auth(\/?|$)/,
      /\/admin\//,
      /^\/ads\/admin(\/?|$)/,
      /^\/ad-banners\/admin(\/?|$)/,
      /^\/users\/admin(\/?|$)/,
      /^\/jobs\/admin(\/?|$)/,
      /^\/audit(\/?|$)/,
      /^\/admin-management(\/?|$)/,
      /^\/review(\/?|$)/,
      /^\/approval(\/?|$)/
    ];
    return adminPatterns.some((re) => re.test(endpoint));
  }

  // Helper method to get auth headers (prefers admin token for admin endpoints)
  getAuthHeaders(endpoint = '') {
    const userToken = localStorage.getItem('token');
    const adminToken = localStorage.getItem('adminToken');
    const preferAdmin = this.isAdminEndpoint(endpoint) || /[?&]includeAll=true\b/.test(endpoint || '');
    // If endpoint is admin, REQUIRE admin token and do not fall back
    const token = preferAdmin ? adminToken : (userToken || adminToken);
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  async ensureHealthyBase() {
    if (this._healthy) return;
    const candidates = [];
    const env = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) ? process.env.REACT_APP_API_URL.trim() : '';
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin.replace(/\/+$/, '') : '';
    // Prefer hosted backend first
    candidates.push('https://jobit-vniz.onrender.com/api');
    if (origin) candidates.push(`${origin}/api`);
    if (env && /^https?:\/\//.test(env)) {
      const cleaned = env.replace(/\/+$/, '');
      const withApi = /\/api(\/?$)/.test(cleaned) ? cleaned : `${cleaned}/api`;
      candidates.push(withApi);
    }
    // Local backend fallback last
    candidates.push('http://localhost:5000/api');

    for (const cand of candidates) {
      try {
        const host = cand.replace(/\/api(\/?$)/, '');
        const resp = await fetch(`${host}/health`, { method: 'GET' });
        if (resp.ok) {
          this.baseURL = cand;
          this._healthy = true;
          if (this.debug) console.log('[ApiService] Selected healthy base:', this.baseURL);
          return;
        }
      } catch (_) { /* try next */ }
    }
    this._healthy = true;
  }

  async refreshAccessTokenIfNeeded(endpoint = '') {
    const { accessKey, refreshKey, refreshEndpoint } = this.getAuthContext(endpoint);
    // STRICT: Only use the appropriate refresh token for the context
    const refreshToken = localStorage.getItem(refreshKey);
    if (!refreshToken) throw new Error('No refresh token available');

    const resp = await fetch(`${this.baseURL}${refreshEndpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });
    const data = await resp.json().catch(() => ({}));
    if (resp.ok && data && data.accessToken) {
      localStorage.setItem(accessKey, data.accessToken);
      if (data.refreshToken) localStorage.setItem(refreshKey, data.refreshToken);
      return data.accessToken;
    }
    // Clear tokens on failed refresh
    localStorage.removeItem(accessKey);
    localStorage.removeItem(refreshKey);
    throw new Error(data?.message || 'Token refresh failed');
  }

  // Generic request method
  async request(endpoint, options = {}) {
    await this.ensureHealthyBase();
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getAuthHeaders(endpoint),
      ...options
    };

    // If sending FormData, let the browser set the Content-Type boundary
    if (config.body instanceof FormData) {
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    }

    try {
      if (this.debug) console.log(`Making API request to: ${url}`);
      const response = await fetch(url, config);
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      let data;
      const isJson = contentType && contentType.includes('application/json');
      
      if (isJson) {
        data = await response.json();
        if (this.debug) console.log(`API response from ${endpoint}:`, data);
      } else {
        // Handle non-JSON responses (HTML error pages, etc.)
        await response.text();
        if (this.debug) console.error(`[ApiService] Non-JSON response (${response.status}) for request`, { endpoint });
        
        // Special fallbacks for known endpoints when backend returns HTML 404
        if (response.status === 404) {
          if (endpoint.includes('/bookings/')) {
            if (this.debug) console.log('Bookings endpoint not found (HTML 404), returning empty array');
            return { bookings: [], success: true };
          }
          if (endpoint.includes('/users/') && endpoint.includes('/saved-jobs')) {
            if (this.debug) console.log('Saved-jobs endpoint not found (HTML 404), returning empty array');
            return { savedJobs: [], success: true };
          }
          if (endpoint.includes('/cvs/')) {
            if (this.debug) console.log('CV endpoint not found (HTML 404), returning empty CV');
            return { success: true, cv: null };
          }

          // Auto-correct missing '/api' base by retrying once with '/api' appended
          const baseHasApi = /\/api$/.test(this.baseURL);
          if (!baseHasApi) {
            const altBase = `${this.baseURL.replace(/\/+$/, '')}/api`;
            const altUrl = `${altBase}${endpoint}`;
            try {
              if (this.debug) console.warn(`[ApiService] Retrying with '/api' appended: ${altUrl}`);
              const altResp = await fetch(altUrl, config);
              const altCT = altResp.headers.get('content-type');
              if (altCT && altCT.includes('application/json')) {
                const altData = await altResp.json();
                if (altResp.ok) {
                  // Persist corrected base for subsequent calls
                  this.baseURL = altBase.replace(/\/+$/, '');
                  if (this.debug) console.log('[ApiService] Base URL corrected to:', this.baseURL);
                  return altData;
                }
                // If JSON but not ok, fall through to common error handling below
                data = altData;
              } else {
                const altText = await altResp.text();
                if (altResp.ok) {
                  this.baseURL = altBase.replace(/\/+$/, '');
                  if (this.debug) console.log('[ApiService] Base URL corrected to:', this.baseURL);
                  return { success: true, message: 'Request completed' };
                }
                throw new Error(`API returned non-JSON response for ${endpoint}. Status: ${altResp.status}. Details: ${altText.substring(0, 200)}...`);
              }
              // Continue to common error path with alt response data
              if (!altResp.ok) {
                const serverMessage = (data && (data.message || data.error || (Array.isArray(data.errors) && data.errors[0]?.message))) || null;
                throw new Error(serverMessage || `HTTP error! status: ${altResp.status}`);
              }
            } catch (retryErr) {
              if (this.debug) console.error('[ApiService] Retry with /api failed:', retryErr);
            }
          }

          // No localhost fallback in production
          // Dev-server 404 fallback: if base points to the frontend origin (e.g., :3001), retry once against :5000/api
          try {
            const isLocalOriginApi = /localhost:3001\/.+\/api$/.test(this.baseURL + '/');
            if (isLocalOriginApi) {
              const altBase2 = (typeof window !== 'undefined' ? window.location.origin.replace(':3001', ':5000') : 'http://localhost:5000').replace(/\/+$/, '') + '/api';
              const altUrl2 = `${altBase2}${endpoint}`;
              if (this.debug) console.warn(`[ApiService] Retrying against backend port: ${altUrl2}`);
              const altResp2 = await fetch(altUrl2, config);
              const altCT2 = altResp2.headers.get('content-type');
              if (altCT2 && altCT2.includes('application/json')) {
                const altData2 = await altResp2.json();
                if (altResp2.ok) {
                  this.baseURL = altBase2.replace(/\/+$/, '');
                  if (this.debug) console.log('[ApiService] Base URL switched to backend:', this.baseURL);
                  return altData2;
                }
                data = altData2;
              } else {
                await altResp2.text();
                if (altResp2.ok) {
                  this.baseURL = altBase2.replace(/\/+$/, '');
                  if (this.debug) console.log('[ApiService] Base URL switched to backend:', this.baseURL);
                  return { success: true, message: 'Request completed' };
                }
                // fall through to common error handling
              }
            }
          } catch (retryErr2) {
            if (this.debug) console.error('[ApiService] Retry against backend port failed:', retryErr2);
          }
        }

        if (!response.ok) {
          throw new Error('Request failed. Please try again later.');
        }
        // For successful non-JSON responses, return a default structure
        data = { success: true, message: 'Request completed' };
      }
      
      if (!response.ok) {
        // Enhanced error handling for JSON responses
        const serverMessage = (data && (data.message || data.error || (Array.isArray(data.errors) && data.errors[0]?.message))) || null;
        switch (response.status) {
          case 400:
            throw new Error(serverMessage || 'Invalid request. Please verify your input and try again.');
          case 401: {
            // Skip refresh for auth endpoints to prevent loops
            const skip = ['/auth/login','/admin-auth/login','/auth/refresh','/admin-auth/refresh','/auth/logout','/admin-auth/logout'];
            if (skip.some(ep => endpoint.startsWith(ep))) {
              throw new Error('Your session has expired. Please log in again.');
            }
            try {
              const newAccess = await this.refreshAccessTokenIfNeeded(endpoint);
              // Retry original request once with refreshed token
              const retryCfg = {
                ...config,
                headers: {
                  ...config.headers,
                  Authorization: `Bearer ${newAccess}`
                }
              };
              const retryResp = await fetch(url, retryCfg);
              const retryCT = retryResp.headers.get('content-type');
              const retryIsJson = retryCT && retryCT.includes('application/json');
              const retryData = retryIsJson ? await retryResp.json() : { success: retryResp.ok };
              if (!retryResp.ok) {
                throw new Error((retryData && (retryData.message || retryData.error)) || 'Your session has expired. Please log in again.');
              }
              return retryData;
            } catch (refreshErr) {
              throw new Error('Your session has expired. Please log in again.');
            }
          }
          case 403:
            throw new Error(serverMessage || 'You do not have permission to perform this action.');
          case 404:
           // For 404 errors, return an empty result instead of throwing an error
           if (endpoint.includes('/bookings/')) {
             if (this.debug) console.log('[ApiService] bookings 404 → empty');
             return { bookings: [], success: true };
           }
           if (endpoint.includes('/users/') && endpoint.includes('/saved-jobs')) {
             if (this.debug) console.log('[ApiService] saved-jobs 404 → empty');
             return { savedJobs: [], success: true };
           }
           if (endpoint.includes('/cvs/')) {
             if (this.debug) console.log('[ApiService] cv 404 → empty');
             return { success: true, cv: null };
           }
            throw new Error(serverMessage || 'The requested resource was not found.');
          case 422:
            throw new Error(serverMessage || 'Please check your input data and try again.');
          default:
            throw new Error(serverMessage || `HTTP error! status: ${response.status}`);
        }
      }

      return data;
    } catch (error) {
      // Enhanced error handling for network and other errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      } else if (error.name === 'AbortError') {
        throw new Error('Request was cancelled. Please try again.');
      } else if (error.message.includes('CORS')) {
        throw new Error('Cross-origin request blocked. Please contact support.');
      }
      
      if (this.debug) console.error('[ApiService] request failed', { endpoint, error });
      // Re-throw the error with original message if it's already a custom error
      throw error;
    }
  }

  // Contact Request APIs
  async createContactRequest(targetUserId, message) {
    return this.request('/contact-requests', {
      method: 'POST',
      body: JSON.stringify({ targetUserId, message })
    });
  }

  // System Settings
  async getSystemSettings() {
    // Prefer public settings when there's no admin session to avoid 401 noise
    const hasAdmin = typeof localStorage !== 'undefined' && !!localStorage.getItem('adminToken');
    const endpoint = hasAdmin ? '/admin/system-settings' : '/public/system-settings';
    const resp = await this.request(endpoint);
    return resp.settings || resp;
  }

  async updateSystemSettings(settings) {
    try {
      const response = await this.request('/admin/system-settings', {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      return response.settings || response;
    } catch (error) {
      console.error('Error updating system settings:', error);
      throw error;
    }
  }

  // Admin: find user by email or id
  async adminFindUser({ email, id }) {
    const params = new URLSearchParams();
    if (email) params.set('email', email);
    if (id) params.set('id', id);
    const response = await this.request(`/admin/users/find?${params.toString()}`);
    return response.user;
  }

  // Admin: get employer quota
  async adminGetEmployerQuota(employerId) {
    const response = await this.request(`/admin/employers/${encodeURIComponent(employerId)}/quota`);
    return response;
  }

  // Admin: set employer quota
  async adminSetEmployerQuota(employerId, { freeJobQuota = null, resetUsed = false } = {}) {
    const response = await this.request(`/admin/employers/${encodeURIComponent(employerId)}/quota`, {
      method: 'PUT',
      body: JSON.stringify({ freeJobQuota, resetUsed })
    });
    return response;
  }

  async getContactRequestStatus(targetUserId) {
    const q = encodeURIComponent(targetUserId);
    return this.request(`/contact-requests/status?targetUserId=${q}`);
  }

  async adminListContactRequests(status = 'rejected') {
    const s = encodeURIComponent(status);
    return this.request(`/admin/contact-requests?status=${s}`);
  }

  async adminAcceptContactRequest(id) {
    return this.request(`/admin/contact-requests/${id}/accept`, { method: 'PUT' });
  }

  async adminRejectContactRequest(id, reason) {
    return this.request(`/admin/contact-requests/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason })
    });
  }

  // Auth-related API calls
  async getCurrentUser() {
    try {
      const response = await this.request('/auth/me');
      return response.user || response;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }

  // Public stats for landing page
  async getPublicStats() {
    try {
      const response = await this.request('/public/stats');
      return response.stats || response;
    } catch (error) {
      console.error('Error fetching public stats:', error);
      throw error;
    }
  }

  // Service-related API calls
  

  async getServiceCategories() {
    try {
      const response = await this.request('/services/categories');
      return response;
    } catch (error) {
      console.error("Error fetching service categories:", error);
      throw error;
    }
  }

  async getFeaturedServices(limit = 6) {
    try {
      const response = await this.request(`/services/featured?limit=${limit}`);
      return response;
    } catch (error) {
      console.error("Error fetching featured services:", error);
      throw error;
    }
  }

  async searchServices(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/services/search${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error searching services:", error);
      throw error;
    }
  }

  async createService(serviceData) {
    try {
      const response = await this.request('/services', {
        method: 'POST',
        body: JSON.stringify(serviceData)
      });
      return response;
    } catch (error) {
      console.error("Error creating service:", error);
      throw error;
    }
  }

  async updateService(id, serviceData) {
    try {
      const response = await this.request(`/services/${id}`, {
        method: 'PUT',
        body: JSON.stringify(serviceData)
      });
      return response;
    } catch (error) {
      console.error("Error updating service:", error);
      throw error;
    }
  }

  async deleteServicePublic(id) {
    try {
      const response = await this.request(`/services/${id}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error("Error deleting service:", error);
      throw error;
    }
  }

  async getServicesByProvider(providerId) {
    try {
      const response = await this.request(`/services/provider/${providerId}`);
      return response.services || [];
    } catch (error) {
      console.error("Error fetching services by provider:", error);
      throw error;
    }
  }

  // Job-related API calls
  async getJobs(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/jobs${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching jobs:", error);
      throw error;
    }
  }

  async getJobById(id) {
    try {
      const response = await this.request(`/jobs/${id}`);
      return response.job || response; // Extract job data from response
    } catch (error) {
      console.error("Error fetching job by ID:", error);
      throw error;
    }
  }

  // Track apply button click for a job (non-blocking)
  async trackJobApplyClick(id) {
    try {
      const response = await this.request(`/jobs/${id}/track-apply-click`, {
        method: 'POST'
      });
      return response.applyClicks ?? (response.data?.applyClicks) ?? 0;
    } catch (error) {
      // Do not surface as blocking; log and continue
      console.warn('Failed to track apply click:', error?.message || error);
      return 0;
    }
  }

  async searchJobs(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/jobs/search${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error searching jobs:", error);
      throw error;
    }
  }

  async getFeaturedJobs(limit = 6) {
    try {
      const response = await this.request(`/jobs/featured?limit=${limit}`);
      return response;
    } catch (error) {
      console.error("Error fetching featured jobs:", error);
      throw error;
    }
  }

  async getJobCategories() {
    try {
      const response = await this.request('/jobs/categories');
      return response;
    } catch (error) {
      console.error("Error fetching job categories:", error);
      throw error;
    }
  }

  // USERS: directory helpers
  async getUsersByRole(role, params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/users/role/${role}${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response; // { success, users, pagination }
    } catch (error) {
      console.error("Error fetching users by role:", error);
      throw error;
    }
  }

  // Admin communications APIs
  async getCommunications(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    const endpoint = `/admin/communications${queryParams ? `?${queryParams}` : ''}`;
    const response = await this.request(endpoint);
    return response;
  }

  // Admin SMS APIs
  async getSmsMessages(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    const endpoint = `/admin/sms${queryParams ? `?${queryParams}` : ''}`;
    const response = await this.request(endpoint);
    return response;
  }

  async createSms(payload = {}) {
    const response = await this.request('/admin/sms', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return response;
  }

  async updateSms(id, payload = {}) {
    const response = await this.request(`/admin/sms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    return response;
  }

  async resendSms(id) {
    const response = await this.request(`/admin/sms/${id}/resend`, { method: 'POST' });
    return response;
  }

  async createCommunication(payload) {
    const response = await this.request('/admin/communications', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return response;
  }

  async updateCommunication(id, payload) {
    const response = await this.request(`/admin/communications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    return response;
  }

  async resendCommunication(id) {
    const response = await this.request(`/admin/communications/${id}/resend`, {
      method: 'POST'
    });
    return response;
  }


  async getEmployers(params = {}) {
    // Use role-based route directly to avoid backend convenience endpoint errors
    return this.getUsersByRole('employer', params);
  }

  async getJobSeekers(params = {}) {
    try {
      const r1 = await this.getUsersByRole('job_seeker', params);
      const u1 = r1?.users || r1?.data?.users || r1?.data?.data?.users || r1?.jobSeekers || r1?.data?.jobSeekers;
      if (Array.isArray(u1)) return r1;
    } catch (_) {}
    try {
      const r2 = await this.getUsersByRole('job-seeker', params);
      const u2 = r2?.users || r2?.data?.users || r2?.data?.data?.users || r2?.jobSeekers || r2?.data?.jobSeekers;
      if (Array.isArray(u2)) return r2;
    } catch (_) {}
    try {
      const r3 = await this.getUsersByRole('jobseeker', params);
      const u3 = r3?.users || r3?.data?.users || r3?.data?.data?.users || r3?.jobSeekers || r3?.data?.jobSeekers;
      if (Array.isArray(u3)) return r3;
    } catch (_) {}
    return { success: true, users: [] };
  }

  // Featured users
  async getFeaturedEmployers(limit = 6) {
    try {
      const response = await this.request(`/users/featured/employers?limit=${encodeURIComponent(limit)}`);
      return response;
    } catch (error) {
      console.error('Error fetching featured employers:', error);
      throw error;
    }
  }

  async getFeaturedJobSeekers(limit = 6) {
    try {
      const response = await this.request(`/users/featured/job-seekers?limit=${encodeURIComponent(limit)}`);
      return response;
    } catch (error) {
      console.error('Error fetching featured job seekers:', error);
      throw error;
    }
  }

  // Ads (public)
  async getAds() {
    try {
      const response = await this.request('/ad-banners');
      return response;
    } catch (error) {
      console.error('Error fetching ads:', error);
      throw error;
    }
  }

  // Ads (admin)
  async adminGetAds() {
    try {
      const response = await this.request('/ad-banners/admin');
      return response;
    } catch (error) {
      console.error('Error fetching admin ads:', error);
      throw error;
    }
  }

  async adminCreateAd({ file, mediaUrl = '', title = '', linkUrl = '', active = true, order = 0 }) {
    try {
      const formData = new FormData();
      if (file) {
        // Prefer 'media' field for backend Cloudinary upload; also include 'image' for backward-compat
        formData.append('media', file);
        formData.append('image', file);
      }
      if (mediaUrl) {
        formData.append('mediaUrl', mediaUrl);
      }
      formData.append('title', title);
      formData.append('linkUrl', linkUrl);
      formData.append('active', String(active));
      formData.append('order', String(order));
      const response = await this.request('/ad-banners/admin', {
        method: 'POST',
        body: formData
      });
      return response;
    } catch (error) {
      console.error('Error creating ad:', error);
      throw error;
    }
  }

  async adminUpdateAd(id, payload) {
    try {
      const response = await this.request(`/ad-banners/admin/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      console.error('Error updating ad:', error);
      throw error;
    }
  }

  async adminDeleteAd(id) {
    try {
      const response = await this.request(`/ad-banners/admin/${encodeURIComponent(id)}`, { method: 'DELETE' });
      return response;
    } catch (error) {
      console.error('Error deleting ad:', error);
      throw error;
    }
  }

  // Admin: boost user profile (employer or job seeker)
  async boostUserProfile(userId, payload = {}) {
    try {
      const response = await this.request(`/users/admin/${encodeURIComponent(userId)}/boost`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error('Error boosting user profile:', error);
      throw error;
    }
  }

  // Admin: cancel a user's boost
  async cancelUserBoost(userId) {
    try {
      const response = await this.request(`/users/admin/${encodeURIComponent(userId)}/boost/cancel`, {
        method: 'PATCH'
      });
      return response;
    } catch (error) {
      console.error('Error canceling user boost:', error);
      throw error;
    }
  }

  // Admin: edit a user's boost (e.g., expiry/date)
  async editUserBoost(userId, payload = {}) {
    try {
      const response = await this.request(`/users/admin/${encodeURIComponent(userId)}/boost/edit`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error('Error editing user boost:', error);
      throw error;
    }
  }

  // Admin: reject a user's boost request
  async rejectUserBoost(userId) {
    try {
      const response = await this.request(`/users/admin/${encodeURIComponent(userId)}/boost/reject`, {
        method: 'PATCH'
      });
      return response;
    } catch (error) {
      console.error('Error rejecting user boost:', error);
      throw error;
    }
  }

  // User: request own boost (optionally include payload like paymentTransactionId)
  async requestUserBoost(userId, payload = undefined) {
    try {
      // Force using USER token for this endpoint (admin tokens are invalid here)
      const userToken = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(userToken ? { Authorization: `Bearer ${userToken}` } : {})
      };
      const options = { method: 'POST', headers };
      if (payload && Object.keys(payload).length > 0) {
        options.body = JSON.stringify(payload);
      }
      const response = await this.request(`/users/${encodeURIComponent(userId)}/boost/request`, options);
      return response;
    } catch (error) {
      console.error('Error requesting user boost:', error);
      throw error;
    }
  }

  // Fetch single user by ID (public profile view)
  async getUserById(id) {
    try {
      const response = await this.request(`/users/${id}`);
      return response.user || response; // { user }
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      throw error;
    }
  }

  async createJob(jobData) {
    try {
      const response = await this.request('/jobs', {
        method: 'POST',
        body: JSON.stringify(jobData)
      });
      return response;
    } catch (error) {
      console.error("Error creating job:", error);
      throw error;
    }
  }

  async updateJob(id, jobData) {
    try {
      const response = await this.request(`/jobs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(jobData)
      });
      return response;
    } catch (error) {
      console.error("Error updating job:", error);
      throw error;
    }
  }

  async deleteJobPublic(id) {
    try {
      const response = await this.request(`/jobs/${id}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error("Error deleting job:", error);
      throw error;
    }
  }

  async getMyJobs(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/jobs/my-jobs${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching my jobs:", error);
      throw error;
    }
  }

  async getSavedJobs(userId) {
    try {
      const response = await this.request(`/users/${userId}/saved-jobs`);
      return response;
    } catch (error) {
      console.error("Error fetching saved jobs:", error);
      throw error;
    }
  }

  async saveJob(userId, jobId) {
    try {
      const response = await this.request(`/users/${userId}/saved-jobs`, {
        method: 'POST',
        body: JSON.stringify({ jobId })
      });
      return response;
    } catch (error) {
      console.error("Error saving job:", error);
      throw error;
    }
  }

  async unsaveJob(userId, jobId) {
    try {
      const response = await this.request(`/users/${userId}/saved-jobs/${jobId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error("Error unsaving job:", error);
      throw error;
    }
  }

  // Service-related API calls
  async getServices(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/services${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching services:", error);
      throw error;
    }
  }

  async getServiceById(id) {
    try {
      const response = await this.request(`/services/${id}`);
      return response.service || response; // Extract service data from response
    } catch (error) {
      console.error("Error fetching service by ID:", error);
      throw error;
    }
  }

  // Booking-related API calls
  async createBooking(bookingData) {
    try {
      const response = await this.request('/bookings', {
        method: 'POST',
        body: JSON.stringify(bookingData)
      });
      return response;
    } catch (error) {
      console.error("Error creating booking:", error);
      throw error;
    }
  }

  async getBookingsByProvider(providerId) {
    try {
      const response = await this.request(`/bookings/provider/${providerId}`);
      return response;
    } catch (error) {
      console.error("Error fetching bookings by provider:", error);
      throw error;
    }
  }

  async updateBookingStatus(bookingId, status) {
    try {
      const response = await this.request(`/bookings/${bookingId}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      return response;
    } catch (error) {
      console.error("Error updating booking status:", error);
      throw error;
    }
  }

  // Admin-related API calls
  async approveJob(jobId, reviewNotes = '') {
    try {
      const payload = reviewNotes ? { reviewNotes } : {};
      const response = await this.request(`/approval/jobs/${jobId}/approve`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error approving job:", error);
      throw error;
    }
  }

  async rejectJob(jobId, reviewNotes) {
    try {
      // reviewNotes is required for rejection
      if (!reviewNotes || !reviewNotes.trim()) {
        throw new Error('Review notes are required for job rejection');
      }
      const response = await this.request(`/approval/jobs/${jobId}/reject`, {
        method: 'PUT',
        body: JSON.stringify({ reviewNotes })
      });
      return response;
    } catch (error) {
      console.error("Error rejecting job:", error);
      throw error;
    }
  }

  async assignJob(jobId, payload = {}) {
    try {
      const response = await this.request(`/admin/jobs/${jobId}/assign`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error assigning job:", error);
      throw error;
    }
  }

  async reassignJob(jobId, payload = {}) {
    try {
      const response = await this.request(`/admin/jobs/${jobId}/reassign`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error reassigning job:", error);
      throw error;
    }
  }

  async dropJob(jobId, payload = {}) {
    try {
      const response = await this.request(`/admin/jobs/${jobId}/drop`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error dropping job:", error);
      throw error;
    }
  }

  async boostJob(jobId, payload = {}) {
    try {
      // Backend mounts jobs router at /api/jobs, and defines
      // admin job boost under /admin/jobs/:id/boost within that router.
      // So the correct full path is /api/jobs/admin/jobs/:id/boost.
      const response = await this.request(`/jobs/admin/jobs/${jobId}/boost`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error boosting job:", error);
      throw error;
    }
  }

  // Employer: request boost for own job (goes to admin approval)
  async requestJobBoost(jobId) {
    try {
      const response = await this.request(`/jobs/${encodeURIComponent(jobId)}/boost/request`, {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Error requesting job boost:', error);
      throw error;
    }
  }

  async cancelJobBoost(jobId) {
    try {
      const response = await this.request(`/jobs/admin/jobs/${encodeURIComponent(jobId)}/boost/cancel`, {
        method: 'PATCH'
      });
      return response;
    } catch (error) {
      console.error("Error canceling job boost:", error);
      throw error;
    }
  }

  async editJobBoost(jobId, payload = {}) {
    try {
      const response = await this.request(`/jobs/admin/jobs/${encodeURIComponent(jobId)}/boost/edit`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error editing job boost:", error);
      throw error;
    }
  }

  async extendJob(jobId, payload = {}) {
    try {
      const response = await this.request(`/admin/jobs/${jobId}/extend`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error extending job:", error);
      throw error;
    }
  }

  async reviveJob(jobId, payload = {}) {
    try {
      const response = await this.request(`/admin/jobs/${jobId}/revive`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error reviving job:", error);
      throw error;
    }
  }

  async resubmitJob(jobId, payload = {}) {
    try {
      const response = await this.request(`/admin/jobs/${jobId}/resubmit`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error resubmitting job:", error);
      throw error;
    }
  }

  async deleteJob(jobId) {
    try {
      const response = await this.request(`/admin/jobs/${jobId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error("Error deleting job:", error);
      throw error;
    }
  }

  async approveService(serviceId, payload = {}) {
    try {
      const response = await this.request(`/approval/services/${serviceId}/approve`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error approving service:", error);
      throw error;
    }
  }

  async rejectService(serviceId, payload = {}) {
    try {
      const response = await this.request(`/approval/services/${serviceId}/reject`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error rejecting service:", error);
      throw error;
    }
  }

  async assignService(serviceId, payload = {}) {
    try {
      const response = await this.request(`/admin/services/${serviceId}/assign`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error assigning service:", error);
      throw error;
    }
  }

  async reassignService(serviceId, payload = {}) {
    try {
      const response = await this.request(`/admin/services/${serviceId}/reassign`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error reassigning service:", error);
      throw error;
    }
  }

  async dropService(serviceId, payload = {}) {
    try {
      const response = await this.request(`/admin/services/${serviceId}/drop`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error dropping service:", error);
      throw error;
    }
  }

  async boostService(serviceId, payload = {}) {
    try {
      const response = await this.request(`/admin/services/${serviceId}/boost`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error boosting service:", error);
      throw error;
    }
  }

  async extendService(serviceId, payload = {}) {
    try {
      const response = await this.request(`/admin/services/${serviceId}/extend`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error extending service:", error);
      throw error;
    }
  }

  async reviveService(serviceId, payload = {}) {
    try {
      const response = await this.request(`/admin/services/${serviceId}/revive`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error reviving service:", error);
      throw error;
    }
  }

  async resubmitService(serviceId, payload = {}) {
    try {
      const response = await this.request(`/admin/services/${serviceId}/resubmit`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return response;
    } catch (error) {
      console.error("Error resubmitting service:", error);
      throw error;
    }
  }

  async deleteService(serviceId) {
    try {
      const response = await this.request(`/admin/services/${serviceId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error("Error deleting service:", error);
      throw error;
    }
  }

  // Admin management API calls
  async getAdmins(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/admin-management${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching admins:", error);
      throw error;
    }
  }

  async getApplications(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/applications${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching applications:", error);
      throw error;
    }
  }

  // Applications by user (job seeker)
  async getApplicationsByUser(userId, params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/applications/user/${userId}${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching user applications:", error);
      throw error;
    }
  }

  // Applications for an employer
  async getApplicationsByEmployer(employerId, params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/applications/employer/${employerId}${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching employer applications:", error);
      throw error;
    }
  }

  // Applications for a specific job
  async getApplicationsByJob(jobId, params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/applications/job/${jobId}${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching job applications:", error);
      throw error;
    }
  }

  // Withdraw/delete an application
  async withdrawApplication(applicationId) {
    try {
      const response = await this.request(`/applications/${applicationId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error("Error withdrawing application:", error);
      throw error;
    }
  }

  async updateApplicationStatus(applicationId, status, notes = '') {
    try {
      const response = await this.request(`/applications/${applicationId}`, {
        method: 'PUT',
        body: JSON.stringify({ status, notes })
      });
      return response;
    } catch (error) {
      console.error("Error updating application status:", error);
      throw error;
    }
  }

  // Booking-related API calls (enhanced)
  async getBookings(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `/bookings${queryParams ? `?${queryParams}` : ''}`;
      const response = await this.request(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching bookings:", error);
      throw error;
    }
  }

  // Bulk action methods for jobs
  async bulkActionJobs(jobIds, action, comment = '') {
    try {
      const response = await this.request('/jobs/bulk-action', {
        method: 'PATCH',
        body: JSON.stringify({ jobIds, action, comment })
      });
      return response;
    } catch (error) {
      console.error("Error performing bulk action on jobs:", error);
      throw error;
    }
  }

  // Individual job action methods (for backward compatibility)
  

  // Bulk action methods for services
  async bulkActionServices(serviceIds, action, comment = '') {
    try {
      const response = await this.request('/services/bulk-action', {
        method: 'PATCH',
        body: JSON.stringify({ serviceIds, action, comment })
      });
      return response;
    } catch (error) {
      console.error("Error performing bulk action on services:", error);
      throw error;
    }
  }

  // Individual service action methods (for backward compatibility)
  

  

}
const apiService = new ApiService();
export default apiService;